<!-- 全局视频播放弹窗：居中 <video> 播放 HLS(.m3u8)。
     优先用 hls.js(MSE，覆盖 Chrome/Firefox/Edge)，hls.js 不支持时回退原生 HLS(Safari/iOS)。
     部分老视频源是私有 OSS 对象(403)，播放失败时给出友好提示。挂在 App.vue 顶层。 -->
<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import { useVideoPlayer } from "@/composables/useVideoPlayer";

const { t } = useI18n({ useScope: "global" });
const { visible, src, title, close } = useVideoPlayer();
const videoEl = ref<HTMLVideoElement | null>(null);
const errored = ref(false);
const loadingVideo = ref(false);

type HlsLike = {
  destroy: () => void;
  loadSource: (s: string) => void;
  attachMedia: (v: HTMLVideoElement) => void;
  on: (ev: string, cb: (e: unknown, data: { fatal?: boolean }) => void) => void;
};
let hls: HlsLike | null = null;

function teardown() {
  if (hls) {
    try {
      hls.destroy();
    } catch {
      /* noop */
    }
    hls = null;
  }
  const v = videoEl.value;
  if (v) {
    v.pause();
    v.removeAttribute("src");
    v.load();
  }
}

async function setup() {
  const v = videoEl.value;
  if (!v || !src.value) return;
  teardown();
  errored.value = false;
  loadingVideo.value = true;

  v.onerror = () => {
    errored.value = true;
    loadingVideo.value = false;
  };
  v.onplaying = () => {
    loadingVideo.value = false;
  };

  try {
    const { default: Hls } = await import("hls.js");
    if (Hls.isSupported()) {
      const inst = new Hls({ enableWorker: true });
      inst.on(Hls.Events.ERROR, (_e: unknown, data: { fatal?: boolean }) => {
        if (data?.fatal) {
          errored.value = true;
          loadingVideo.value = false;
        }
      });
      inst.loadSource(src.value);
      inst.attachMedia(v);
      hls = inst as unknown as HlsLike;
      v.play().catch(() => {});
      return;
    }
  } catch {
    /* hls.js 加载失败，走原生 */
  }

  // 原生 HLS（Safari / iOS）
  if (v.canPlayType("application/vnd.apple.mpegurl")) {
    v.src = src.value;
    v.play().catch(() => {});
  } else {
    errored.value = true;
    loadingVideo.value = false;
  }
}

watch([visible, src], async ([vis]) => {
  if (vis && src.value) {
    await Promise.resolve();
    await Promise.resolve();
    setup();
  } else {
    teardown();
  }
});

onBeforeUnmount(teardown);
</script>

<template>
  <van-popup
    :show="visible"
    round
    :style="{ width: 'min(92vw, 880px)', background: '#000' }"
    teleport="body"
    @update:show="(v: boolean) => { if (!v) close(); }"
  >
    <div class="relative bg-black">
      <button
        class="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-black/60 text-white grid place-items-center hover:bg-black/80"
        @click="close"
      >
        ✕
      </button>

      <!-- 播放失败提示（私有源 403 等） -->
      <div
        v-if="errored"
        class="aspect-video w-full grid place-items-center text-center px-6"
      >
        <div>
          <div class="text-4xl mb-3">😶‍🌫️</div>
          <div class="text-white/90 text-sm">{{ t('detail.videoUnavailable') }}</div>
          <div class="text-white/50 text-xs mt-1">{{ t('detail.videoUnavailableDesc') }}</div>
        </div>
      </div>

      <video
        v-show="!errored"
        ref="videoEl"
        class="w-full max-h-[78vh] bg-black"
        controls
        autoplay
        playsinline
      ></video>

      <div v-if="title" class="px-4 py-2 text-sm text-white/85 line-clamp-1 bg-black">
        {{ title }}
      </div>
    </div>
  </van-popup>
</template>
